let apiUrl = ''

export function configureAuth(url: string) {
  apiUrl = url
}

export function getApiUrl(): string {
  return apiUrl
}

// Access token: メモリのみ (XSS 노출 최소화)
// Refresh token: httpOnly cookie (Workers API가 Set-Cookie로 설정, JS에서 접근 불가)
let memoryAccessToken: string | null = null

// 개발 환경에서만: HMR/페이지 재로드 시 토큰 손실 방지용 sessionStorage 폴백
// sessionStorage는 탭 단위로 유지됨 (창 닫기 시 삭제)
const IS_DEV = typeof window !== 'undefined' && (import.meta as { env?: { DEV?: boolean } }).env?.DEV === true
const DEV_TOKEN_KEY = '_dev_access_token'

export function setTokens(access: string) {
  memoryAccessToken = access
  if (IS_DEV) {
    try { sessionStorage.setItem(DEV_TOKEN_KEY, access) } catch { /* ignore */ }
  }
}

export function getAccessToken(): string | null {
  if (!memoryAccessToken && IS_DEV) {
    try {
      const stored = sessionStorage.getItem(DEV_TOKEN_KEY)
      if (stored) memoryAccessToken = stored
    } catch { /* ignore */ }
  }
  return memoryAccessToken
}

export function clearTokens() {
  memoryAccessToken = null
  if (IS_DEV) {
    try { sessionStorage.removeItem(DEV_TOKEN_KEY) } catch { /* ignore */ }
  }
  // refresh_token cookie는 Workers API /auth/logout에서 Max-Age=0으로 삭제됨
}

// 동시 다발 요청의 중복 refresh 방지 (mutex)
let refreshPromise: Promise<string | null> | null = null

export async function refreshAccessToken(): Promise<string | null> {
  if (refreshPromise) return refreshPromise

  refreshPromise = doRefresh()
  try {
    return await refreshPromise
  } finally {
    refreshPromise = null
  }
}

async function doRefresh(): Promise<string | null> {
  try {
    // refresh_token은 httpOnly cookie로 자동 전송됨 — body 불필요, credentials: 'include' 필수
    const res = await fetch(`${apiUrl}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
    })

    if (!res.ok) {
      clearTokens()
      return null
    }

    const data = (await res.json()) as { accessToken: string }
    setTokens(data.accessToken)
    return data.accessToken
  } catch {
    clearTokens()
    return null
  }
}

/** JWT payload에서 sub, role, churchId 추출 (검증 없이 디코딩만) */
export function parseJwtPayload(token: string): {
  sub: string
  role: string
  churchId: string
  exp: number
} | null {
  try {
    const parts = token.split('.')
    if (parts.length !== 3) return null
    const payload = JSON.parse(atob(parts[1]!.replace(/-/g, '+').replace(/_/g, '/')))
    return payload
  } catch {
    return null
  }
}

/** 토큰이 만료되었거나 1분 이내 만료 예정인지 확인 */
export function isTokenExpired(token: string): boolean {
  const payload = parseJwtPayload(token)
  if (!payload) return true
  return payload.exp * 1000 < Date.now() + 60_000
}
